import React, { memo } from 'react';
import {
  Box,
  Card,
  Chip,
  Stack,
  Typography,
  alpha,
  useTheme,
  IconButton,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import {
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  ShoppingCart as ShoppingCartIcon,
  ShoppingCartOutlined as ShoppingCartOutlinedIcon,
} from '@mui/icons-material';
import LazyImage from '../LazyImage';
import CardOwnershipToggle from '../CardOwnershipToggle/CardOwnershipToggle';

export interface GameCardItemProps {
  card: {
    id: string;
    name: string;
    images: {
      small?: string;
      large: string;
    };
    number: string;
    rarity?: string;
    color?: string[];
    types?: string[];
    justtcgName?: string;
    setName?: string;
  };
  isOwned: boolean;
  isFavorite: boolean;
  isWishlist: boolean;
  onCardClick: (card: any) => void;
  onToggleOwnership: (cardId: string) => void;
  onToggleWishlist: (cardId: string) => void;
  onToggleFavorite: (cardId: string) => void;
  isToggling: boolean;
}

const GameCardItem = memo(
  ({
    card,
    isOwned,
    isFavorite,
    isWishlist,
    onCardClick,
    onToggleOwnership,
    onToggleWishlist,
    onToggleFavorite,
    isToggling,
  }: GameCardItemProps) => {
    const theme = useTheme();
    const { isAuthenticated } = useAuth();
    const { showError } = useNotification();

    const handleAction = (e: React.MouseEvent, action: () => void) => {
      e.stopPropagation();
      if (!isAuthenticated) {
        showError('Please sign in to manage your collection');
        return;
      }
      action();
    };

    const getRarityColor = (rarity: string) => {
      // One Piece rarities
      if (['SR', 'SEC', 'SP', 'L'].includes(rarity)) return 'warning';
      
      // Pokemon rarities
      switch (rarity) {
        case 'Ultra Rare':
        case 'Secret Rare':
          return 'error';
        case 'Rare':
        case 'Holo Rare':
          return 'warning';
        case 'Uncommon':
          return 'info';
        default:
          return 'default';
      }
    };

    return (
      <Card
        onClick={() => onCardClick(card)}
        sx={{
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          cursor: 'pointer',
          position: 'relative',
          transition: 'transform 0.2s',
          backgroundColor: isOwned
            ? alpha(theme.palette.success.main, 0.15)
            : 'transparent',
          border: isOwned
            ? `3px solid ${theme.palette.success.main}`
            : 'none',
          boxShadow: isOwned
            ? `0 0 8px ${alpha(theme.palette.success.main, 0.4)}`
            : undefined,
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          },
        }}
      >
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              left: 4,
              zIndex: 2,
            }}
            onClick={(e) => e.stopPropagation()} 
          >
            <CardOwnershipToggle
              isOwned={isOwned}
              onToggle={() => onToggleOwnership(card.id)}
              isLoading={isToggling}
              sx={{
                backgroundColor: isOwned
                  ? theme.palette.success.main
                  : alpha(theme.palette.background.paper, 0.8),
                '&:hover': {
                  backgroundColor: isOwned
                    ? theme.palette.success.dark
                    : alpha(theme.palette.background.paper, 0.95),
                },
              }}
            />
          </Box>

          <Box sx={{ position: 'relative', width: '100%' }}>
            <LazyImage
              src={card.images.small || card.images.large}
              alt={card.justtcgName || card.name}
              placeholderHeight={280}
              style={{
                width: '100%',
                height: '280px',
                objectFit: 'contain',
                background: alpha(theme.palette.background.paper, 0.5),
                marginBottom: '8px',
              }}
            />
            
            <IconButton
              size="small"
              onClick={(e) => handleAction(e, () => onToggleWishlist(card.id))}
              sx={{
                position: 'absolute',
                bottom: 12,
                left: 4,
                zIndex: 2,
                backgroundColor: isWishlist 
                  ? theme.palette.info.main 
                  : alpha(theme.palette.background.paper, 0.7),
                color: isWishlist ? '#fff' : 'text.secondary',
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: isWishlist 
                    ? theme.palette.info.dark 
                    : alpha(theme.palette.background.paper, 0.9),
                }
              }}
            >
              {isWishlist ? <ShoppingCartIcon fontSize="small" /> : <ShoppingCartOutlinedIcon fontSize="small" />}
            </IconButton>

            <IconButton
              size="small"
              onClick={(e) => handleAction(e, () => onToggleFavorite(card.id))}
              sx={{
                position: 'absolute',
                bottom: 12,
                right: 4,
                zIndex: 2,
                backgroundColor: isFavorite 
                  ? theme.palette.error.main 
                  : alpha(theme.palette.background.paper, 0.7),
                color: isFavorite ? '#fff' : 'text.secondary',
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: isFavorite 
                    ? theme.palette.error.dark 
                    : alpha(theme.palette.background.paper, 0.9),
                }
              }}
            >
              {isFavorite ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
            </IconButton>
          </Box>

          <Typography
            variant="body2"
            fontWeight="medium"
            noWrap
            sx={{ width: '100%', textAlign: 'center' }}
          >
            {card.justtcgName || card.name}
          </Typography>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            justifyContent="center"
            sx={{ mt: 1, width: '100%' }}
          >
            {card.color && card.color.map((color) => (
              <Chip
                key={color}
                label={color.charAt(0)}
                size="small"
                sx={{
                  height: 16,
                  minWidth: 16,
                  fontSize: '0.6rem',
                  backgroundColor: getColorChipColor(color),
                  color: '#fff',
                }}
              />
            ))}
            {card.types && card.types.map((type) => (
              <Chip
                key={type}
                label={type}
                size="small"
                sx={{ 
                  height: 16, 
                  fontSize: '0.6rem',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  borderColor: alpha(theme.palette.primary.main, 0.2)
                }}
                variant="outlined"
              />
            ))}
            <Chip
              label={card.number}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
            {card.rarity && (
              <Chip
                label={card.rarity}
                size="small"
                color={getRarityColor(card.rarity) as any}
                sx={{ fontSize: '0.7rem' }}
              />
            )}
            {(card.rarity === 'L' || (card as any).category === 'Leader') && (
              <Chip
                label="Leader"
                size="small"
                color="primary"
                sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}
              />
            )}
           </Stack>
           {card.setName && (
             <Typography 
               variant="caption" 
               color="text.secondary" 
               sx={{ mt: 0.5, fontSize: '0.65rem', opacity: 0.8 }}
               noWrap
             >
               {card.setName}
             </Typography>
           )}
      </Card>
    );
  },
  (prev, next) => {
    return (
      prev.isOwned === next.isOwned &&
      prev.isFavorite === next.isFavorite &&
      prev.isWishlist === next.isWishlist &&
      prev.isToggling === next.isToggling &&
      prev.card.id === next.card.id &&
      prev.card.name === next.card.name &&
      prev.card.justtcgName === next.card.justtcgName &&
      prev.card.images?.small === next.card.images?.small &&
      prev.card.images?.large === next.card.images?.large
    );
  }
);

const getColorChipColor = (color: string): string => {
  const colorMap: { [key: string]: string } = {
    Red: '#e74c3c',
    Blue: '#3498db',
    Green: '#2ecc71',
    Purple: '#9b59b6',
    Black: '#34495e',
    Yellow: '#f39c12',
  };
  return colorMap[color] || '#95a5a6';
};

export default GameCardItem;
